import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { Character, CharacterSheetInterface } from "@dndModels";

import CharacterAbilityScoresInput from "@dndComponents/character/ability_scores/CharacterAbilityScoresInput";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import FloatMenu from "@components/input/menus/FloatMenu";
import CharacterFeatureFromSource from "@dndComponents/character/features/CharacterFeatureFromSource";
import CharacterStatsCustomizations from '../customizations/CharacterStatsCustomizations';

interface Props {
  char: CharacterSheetInterface;
  className: string;
  disabled: boolean;
  onChange: () => void;
}

export default function CharacterAbilityScoresButton({
  char,
  className,
  disabled,
}: Props) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("Base");
  const [ping, setPing] = useState(false);
  const [reloading, setReloading] = useState(false);

  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);

  const recalculated = useCallback(() => {
    // console.log(`Ability Scores Button - Recalculated`);
    setTimeout(() => {
      setReloading(true);
    }, 100);
  }, []);

  useEffect(() => {
    char.subscription_holder.subscribe("Recalculated", mounted, recalculated);
    recalculated();
  }, [recalculated]);

  const renderExtras = () => {
    return (
      <ModalOrDrawer
        open={open}
        onClose={() => { 
          setOpen(false);
          char.subscription_holder.modalClosed(); 
        }}
      >
        { open && renderAbilityScoresInput() }
      </ModalOrDrawer>
    );
  }

  const renderBase = () => {
    if (char instanceof Character) {
      return (
        <CharacterAbilityScoresInput
          character={char}
        /> 
      );
    }
    return null;
  }

  const renderASIFeatures = () => {
    const asi_features = [...char.mind.asi_features];
    // console.log(asi_features);
    // const cfb_ids = asi_features.map(o => o)
    return (
      <>
        <Grid size={12}
          className="header">
          Ability Score Features
        </Grid>
        { asi_features.sort((a,b) => { return a.name.localeCompare(b.name) }).map((char_feature, key) => {
          const source = char.char_feature_sources.find(o => 
            o.true_id === char_feature.source_true_id);
          if (source) {
            return (
              <Grid size={12}
                key={key}>
                <CharacterFeatureFromSource
                  char={char}
                  obj={char_feature}
                  source={source}
                />
              </Grid>
            );
          }
          return null;
        })}
      </>
    );
  }

  const renderView = () => {
    if (char instanceof Character) {
      switch(view) {
        case "Base": return renderBase();
        case "Features": return renderASIFeatures();
        case "Customizations": 
          return (
            <CharacterStatsCustomizations 
              char={char} 
            />
          );
      }
    } else {
      return (
        <CharacterStatsCustomizations 
          char={char} 
        />
      );
    }
    return null;
  }

  const renderAbilityScoresInput = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12}
          className="centered header">
          Manage Ability Scores
        </Grid>
        { char instanceof Character &&
          <Grid size={12}>
            <FloatMenu
              options={["Base","Features","Customizations"]}
              selected={view}
              onSelect={setView}
            />
          </Grid>
        }
        { renderView() }
      </Grid>
    );
  }

  if (disabled) {
    return (
      <div className={ `header centered ${className}`}>
        Ability Scores
      </div>
    ); 
  } else {
    return (
      <>
        <Tooltip title={`Change Ability Scores`}>
          <div className={ `clickable header centered ${className}`}
            onClick={() => { setOpen(true); }}>
            Ability Scores
          </div>
        </Tooltip>
        { renderExtras() }
      </>
    ); 
  }
}

